import { useCallback } from "react";
import { AxiosError } from "axios";
import { apiInstance } from "@repo/core/services/instances/api";
import { ClientResource } from "@repo/core/services/resources/client";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { PROPOSTA_SITUACAO } from "@repo/core/services/entities/Proposals";
import {
  ConsultProfileProposalsResponseDTO,
  LstCartao
} from "@repo/core/services/entities/Client";
import { ConsultProposalResponseDTO } from "@repo/core/services/entities/Proposals";

const clientResource = new ClientResource(apiInstance);
const proposalsResource = new ProposalsResource(apiInstance);

export enum ProposalFlowType {
  ANALYSIS = "ANALYSIS",
  REANALYSIS = "REANALYSIS"
}

export enum ProposalValidationAction {
  ANALYSIS = "ANALYSIS",
  REANALYSIS = "REANALYSIS",
  BLOCK = "BLOCK",
  LOAD_DRAFT = "LOAD_DRAFT"
}

export interface ProposalValidationResult {
  action: ProposalValidationAction;
  flowType?: ProposalFlowType;
  proposalId?: string;
  proposalDetails?: ConsultProposalResponseDTO;
  clientName?: string;
  message?: string;
  error?: string;
  /** First active card from lstCartoes[0] — cached to avoid a duplicate /ConsultarPerfilPropostas call on reanalysis confirmation */
  firstCardInfo?: LstCartao;
}

type LstProposta = ConsultProfileProposalsResponseDTO["lstPropostas"][number];

const normalizeProductCode = (productCode: string): string =>
  productCode.trim();

export const filterProfileProposalsByProduct = (
  profile: ConsultProfileProposalsResponseDTO,
  productCode: string
): ConsultProfileProposalsResponseDTO => {
  const normalizedProductCode = normalizeProductCode(productCode);

  if (!normalizedProductCode) {
    return profile;
  }

  return {
    lstCartoes: (profile.lstCartoes ?? []).filter(
      (card) => normalizeProductCode(card.strProduto) === normalizedProductCode
    ),
    lstPropostas: (profile.lstPropostas ?? []).filter(
      (proposal) =>
        normalizeProductCode(proposal.strProduto) === normalizedProductCode
    )
  };
};

/**
 * Finds the most recent proposal from the list
 * Orders by datDataSituacao (descending), then by lngProposta (descending) if dates are equal
 * @param lstPropostas - Array of proposals
 * @returns The most recent proposal or null if array is empty
 */
const findMostRecentProposal = (
  lstPropostas: LstProposta[]
): LstProposta | null => {
  if (lstPropostas.length === 0) {
    return null;
  }

  if (lstPropostas.length === 1) {
    return lstPropostas[0];
  }

  const sorted = [...lstPropostas].sort((a, b) => {
    const dateA = new Date(a.datDataSituacao).getTime();
    const dateB = new Date(b.datDataSituacao).getTime();

    if (dateA !== dateB) {
      return dateB - dateA;
    }

    const proposalIdA = parseInt(a.lngProposta, 10);
    const proposalIdB = parseInt(b.lngProposta, 10);

    return proposalIdB - proposalIdA;
  });

  return sorted[0];
};

interface StatusActionConfig {
  action: ProposalValidationAction;
  message?: string;
  flowType?: ProposalFlowType;
}

/**
 * Complete status matrix mapping each status to action, message and flow type
 * Based on business rules specification
 */
const STATUS_ACTION_MAP: Record<string, StatusActionConfig> = {
  // Block - Proposal in Analysis (1, 2, 3, 4, 12)
  [PROPOSTA_SITUACAO.QUEUE_ANALYSIS_AUTOMATIC]: {
    action: ProposalValidationAction.BLOCK,
    message: "Já existe uma proposta em análise. Aguarde."
  },
  [PROPOSTA_SITUACAO.WAITING_FOR_ANALYSIS]: {
    action: ProposalValidationAction.BLOCK,
    message: "Já existe uma proposta em análise. Aguarde."
  },
  [PROPOSTA_SITUACAO.ANALYSIS_AUTOMATIC]: {
    action: ProposalValidationAction.BLOCK,
    message: "Já existe uma proposta em análise. Aguarde."
  },
  [PROPOSTA_SITUACAO.ANALYSIS_MANUAL]: {
    action: ProposalValidationAction.BLOCK,
    message: "Já existe uma proposta em análise. Aguarde."
  },
  [PROPOSTA_SITUACAO.MANUAL_ANALYSIS_QUEUE]: {
    action: ProposalValidationAction.BLOCK,
    message: "Já existe uma proposta em análise. Aguarde."
  },

  // Block - Proposal Analyzed (5, 6, 7, 8)
  [PROPOSTA_SITUACAO.APPROVED_AUTOMATICALLY]: {
    action: ProposalValidationAction.BLOCK,
    message:
      "Já existe uma proposta analisada para este produto. Verifique a Fila de Análise."
  },
  [PROPOSTA_SITUACAO.APPROVED_MANUALLY]: {
    action: ProposalValidationAction.BLOCK,
    message:
      "Já existe uma proposta analisada para este produto. Verifique a Fila de Análise."
  },
  [PROPOSTA_SITUACAO.REJECTED_AUTOMATICALLY]: {
    action: ProposalValidationAction.BLOCK,
    message:
      "Já existe uma proposta analisada para este produto. Verifique a Fila de Análise."
  },
  [PROPOSTA_SITUACAO.REJECTED_MANUALLY]: {
    action: ProposalValidationAction.BLOCK,
    message:
      "Já existe uma proposta analisada para este produto. Verifique a Fila de Análise."
  },

  // Analysis - Continue Flow (9, 10, 13, 14)
  [PROPOSTA_SITUACAO.PENDING_CORRECTION]: {
    action: ProposalValidationAction.ANALYSIS,
    flowType: ProposalFlowType.ANALYSIS
  },
  [PROPOSTA_SITUACAO.PENDING_ANALYSIS]: {
    action: ProposalValidationAction.ANALYSIS,
    flowType: ProposalFlowType.ANALYSIS
  },
  // Reanalysis - Effective proposal (reference for new reanalysis draft)
  [PROPOSTA_SITUACAO.EFFECTIVE]: {
    action: ProposalValidationAction.REANALYSIS,
    flowType: ProposalFlowType.REANALYSIS
  },
  [PROPOSTA_SITUACAO.ERROR_ANALYSIS]: {
    action: ProposalValidationAction.ANALYSIS,
    flowType: ProposalFlowType.ANALYSIS
  },
  [PROPOSTA_SITUACAO.CANCELLED]: {
    action: ProposalValidationAction.ANALYSIS,
    flowType: ProposalFlowType.ANALYSIS
  },

  // Block - Pre-proposal in Analysis (15, 18)
  [PROPOSTA_SITUACAO.PRE_PROPOSAL]: {
    action: ProposalValidationAction.BLOCK,
    message: "Já existe uma pré-proposta em análise. Aguarde o resultado."
  },
  [PROPOSTA_SITUACAO.PRE_PROPOSAL_ANALYSIS]: {
    action: ProposalValidationAction.BLOCK,
    message: "Já existe uma pré-proposta em análise. Aguarde o resultado."
  },

  // Block - Pre-proposal Analyzed (16, 17)
  [PROPOSTA_SITUACAO.PRE_PROPOSAL_APPROVED]: {
    action: ProposalValidationAction.BLOCK,
    message:
      "Já existe pré-proposta analisada para este cliente. Prossiga pela Fila de Análise."
  },
  [PROPOSTA_SITUACAO.PRE_PROPOSAL_REJECTED]: {
    action: ProposalValidationAction.BLOCK,
    message:
      "Já existe pré-proposta analisada para este cliente. Prossiga pela Fila de Análise."
  },

  // Load Draft - Pre-registered (19, 20)
  [PROPOSTA_SITUACAO.DOCUMENT_CONFERENCE]: {
    action: ProposalValidationAction.LOAD_DRAFT
    //message: "Cliente pré-cadastrado. Atualizar dados na proposta."
  },
  [PROPOSTA_SITUACAO.DATA_CONFERENCE]: {
    action: ProposalValidationAction.LOAD_DRAFT
    //message: "Cliente pré-cadastrado. Atualizar dados na proposta."
  }
};

/**
 * Determines the initial action based on cards and proposals data
 * @param lstCartoes - Array of cards
 * @param lstPropostas - Array of proposals
 * @returns ProposalValidationAction or null if needs to check proposal status
 */
const determineInitialAction = (
  lstCartoes: LstCartao[],
  lstPropostas: LstProposta[]
): ProposalValidationAction | null => {
  if (lstCartoes.length > 0) {
    return ProposalValidationAction.REANALYSIS;
  }

  if (lstPropostas.length === 0) {
    return ProposalValidationAction.ANALYSIS;
  }

  return null;
};

/**
 * Determines action and message based on proposal status using status matrix
 * @param strSituacao - Proposal status
 * @returns StatusActionConfig with action, message and flowType
 */
const getStatusAction = (strSituacao: string): StatusActionConfig => {
  const config = STATUS_ACTION_MAP[strSituacao];

  if (config) {
    return config;
  }

  return {
    action: ProposalValidationAction.BLOCK,
    message: `Status da proposta em: ${strSituacao}", não identificado. Entre em contato com o suporte.`
  };
};

/** Resolves validation action/message for a proposal status (shared by analysis and reanalysis). */
export const resolveProposalStatusValidation = (
  strSituacao: string
): StatusActionConfig => getStatusAction(strSituacao);

/**
 * Hook to validate proposals and determine the correct flow (analysis or reanalysis)
 */
export const useProposalValidation = () => {
  /**
   * Validates proposals for a given CPF and determines the flow type
   * @param cpf - Client CPF
   * @param productCode - Selected product code
   * @returns Promise with validation result
   */
  const validateProposals = useCallback(
    async (
      cpf: string,
      productCode: string
    ): Promise<ProposalValidationResult> => {
      try {
        const { data: rawProfile } =
          await clientResource.consultProfileProposals(cpf);

        const consultProfileResponse = filterProfileProposalsByProduct(
          rawProfile ?? { lstCartoes: [], lstPropostas: [] },
          productCode
        );

        const lstCartoes = consultProfileResponse.lstCartoes ?? [];
        const lstPropostas = consultProfileResponse.lstPropostas ?? [];

        const initialAction = determineInitialAction(lstCartoes, lstPropostas);

        if (initialAction !== null) {
          const mostRecentProposal =
            initialAction === ProposalValidationAction.REANALYSIS
              ? findMostRecentProposal(lstPropostas)
              : null;

          const hasActiveCard =
            initialAction === ProposalValidationAction.REANALYSIS &&
            lstCartoes.length > 0;

          return {
            action: initialAction,
            flowType: hasActiveCard
              ? ProposalFlowType.REANALYSIS
              : ProposalFlowType.ANALYSIS,
            proposalId: mostRecentProposal?.lngProposta,
            clientName: hasActiveCard ? lstCartoes[0].strNome : undefined,
            // Cache the first card so reanalysis confirmation can use it
            // without making a second /ConsultarPerfilPropostas call
            firstCardInfo: hasActiveCard ? lstCartoes[0] : undefined
          };
        }

        if (lstPropostas.length > 0) {
          const mostRecentProposal = findMostRecentProposal(lstPropostas);

          if (!mostRecentProposal) {
            return {
              action: ProposalValidationAction.ANALYSIS,
              flowType: ProposalFlowType.ANALYSIS
            };
          }

          const proposalId = mostRecentProposal.lngProposta;

          const { data: proposalDetails } =
            await proposalsResource.getProposalDetails(proposalId);

          const statusConfig = getStatusAction(proposalDetails.strSituacao);

          const proposalDetailsTyped =
            proposalDetails as unknown as ConsultProposalResponseDTO;

          return {
            action: statusConfig.action,
            flowType: statusConfig.flowType,
            proposalId,
            proposalDetails: proposalDetailsTyped,
            clientName: proposalDetailsTyped?.strNome,
            message: statusConfig.message
          };
        }

        return {
          action: ProposalValidationAction.ANALYSIS,
          flowType: ProposalFlowType.ANALYSIS
        };
      } catch (error) {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          // No registered profile / client — continue as new pre-proposal (first analysis)
          if (status === 404 || status === 204) {
            return {
              action: ProposalValidationAction.ANALYSIS,
              flowType: ProposalFlowType.ANALYSIS
            };
          }
        }

        console.error("Error validating proposals:", error);
        return {
          action: ProposalValidationAction.ANALYSIS,
          flowType: ProposalFlowType.ANALYSIS,
          error: "Erro ao validar propostas. Tente novamente."
        };
      }
    },
    []
  );

  return {
    validateProposals
  };
};
